import * as yup from "yup";
import { capitalize } from "@mui/material";
import { FormikConfig, FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { inboundApiUrl, telefonnummer } from "../../config";
import { TestCallDatenAiToAi } from "../../api";
import { useAiToAiCall } from "../../client/hooks";

export interface AiToAiFormValues {
  telefonnummer: string;
  externalAiConfigEndpointUrl: string;
  externalSpeechModelPrompt: string;
}

interface Options {
  onSuccess?: () => void;
}

export function useAiToAiTestCallForm(
  aufgabeId: string,
  options?: Options
): FormikConfig<AiToAiFormValues> {
  const { t: translation } = useTranslation("aufgaben");
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const aiToAiCall = useAiToAiCall();

  const schema = yup.object().shape({
    externalSpeechModelPrompt: yup
      .string()
      .max(2000, capitalize(translation("validation-max-2000-characters"))),
  });

  const initialTouched = useMemo(
    () => ({
      telefonnummer: false,
      externalAiConfigEndpointUrl: false,
      externalSpeechModelPrompt: false,
    }),
    []
  );

  const initialValues = useMemo(
    () => ({
      telefonnummer: telefonnummer,
      externalAiConfigEndpointUrl: inboundApiUrl,
      externalSpeechModelPrompt: "",
    }),
    []
  );

  const { mutateAsync } = useMutation(
    async (input: TestCallDatenAiToAi) => aiToAiCall(input),
    {
      onSuccess: async (_data, variables) => {
        if (variables.aufgabeId) {
          await queryClient.invalidateQueries(["aufgaben", variables.aufgabeId]);
        }
        enqueueSnackbar(capitalize(translation("call-successfully-scheduled")), {
          variant: "success",
        });
        options?.onSuccess?.();
      },
      onError: (error: any) => {
        enqueueSnackbar(error?.message || capitalize(translation("call-failed")), {
          variant: "error",
        });
      },
    }
  );

  const onSubmit = useCallback(
    async (
      values: AiToAiFormValues,
      { setSubmitting, setStatus, resetForm }: FormikHelpers<AiToAiFormValues>
    ): Promise<void> => {
      try {
        await mutateAsync({
          aufgabeId,
          telefonnummer: values.telefonnummer,
          externalAiConfigEndpointUrl: values.externalAiConfigEndpointUrl,
          externalSpeechModelPrompt: values.externalSpeechModelPrompt || null,
        });
        setStatus(undefined);
        resetForm();
      } catch (error: unknown) {
        if (error instanceof Error) {
          setStatus(error.message);
        } else {
          setStatus(capitalize(translation("call-failed")));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [mutateAsync, aufgabeId, translation]
  );

  return useMemo(
    () => ({
      validationSchema: schema,
      validateOnMount: true,
      enableReinitialize: true,
      initialTouched,
      initialValues,
      onSubmit,
    }),
    [schema, initialTouched, initialValues, onSubmit]
  );
}
